// ─── I livelli a parole: la scala è del circolo ──────────────────────────────
//
// 🗣️ Dal documento dei flussi (21/09/2026): *«Il livello esce come parola, mai
// come numero»*. E il 25/09/2026, per la scheda del socio: *«il livello gioco
// dentro la scheda socio deve essere una tendina con i livelli a parole non a
// numeri»* — e le parole anche nella lista dei soci (scelta B del mockup).
//
// ⚖️⭐⭐ **PERCHÉ STA QUI, IN UN POSTO SOLO.** In tabella il livello è un numero
// (`members.level`, `numeric(3,1)`); la parola la decide la scala del circolo
// (`app_settings.conoscenza_soci.livelli.fasce`). La leggono il ponte del bot e
// la scheda del socio: una tabella di conversione per ciascuno sarebbe una
// seconda copia della scala, e due copie si accorgono di essere diverse il
// giorno in cui qualcuno cambia la scala — cioè accanto a partite di persone
// vere.
// 📌 *La scala di un circolo è un dato del circolo, non una costante di un
//    programma che la mostra.*
//
// ⛔ **E se il circolo non dichiara nessuna fascia, la parola è NULLA**: chi la
// mostra non dice nessun livello a parole, invece di tradurlo con una scala
// inventata qui dentro.
import { z } from 'zod';
/** Una fascia della scala del circolo: «da 3,0 a 3,5 si chiama *Intermedio*». */
export const fasciaDiLivelloSchema = z.object({
    da: z.number(),
    a: z.number(),
    parola: z.string().trim().min(1),
});
/** `GET /members/livelli`: la scala, in sola lettura, per chi guarda i soci. */
export const scalaDeiLivelliSchema = z.object({ fasce: z.array(fasciaDiLivelloSchema) });
/**
 * La parola con cui il circolo chiama quel livello.
 *
 * ⚠️ Estremi **inclusi** tutti e due, e le fasce si leggono in ordine: la prima
 * che contiene il numero vince. Se la scala ha buchi o sovrapposizioni è la
 * scala del circolo ad averli — non si aggiusta qui, o la parola mostrata non
 * sarebbe più quella che la segreteria crede di aver scritto.
 */
export function parolaDelLivello(fasce, livello) {
    if (livello === null)
        return null;
    return fasce.find((f) => livello >= f.da && livello <= f.a)?.parola ?? null;
}
export function sceltaDelLivello(fasce, livello) {
    if (livello === null)
        return null;
    const i = fasce.findIndex((f) => livello >= f.da && livello <= f.a);
    return i === -1 ? 'fuori' : i;
}
/**
 * Il numero da salvare per una scelta fatta nella tendina.
 *
 * ⭐ **Il numero di prima non si perde**: se la parola scelta è quella della
 * fascia in cui il livello cade già, si rimanda il livello di prima — un 3,5
 * resta 3,5 anche se la tendina dice solo «Intermedio». Solo cambiando fascia
 * il livello diventa il primo numero della fascia nuova.
 */
export function livelloDaSalvare(fasce, attuale, scelta) {
    if (scelta === null)
        return null;
    if (scelta === 'fuori')
        return attuale;
    const fascia = fasce[scelta];
    if (!fascia)
        return attuale;
    if (attuale !== null && attuale >= fascia.da && attuale <= fascia.a)
        return attuale;
    return fascia.da;
}
/**
 * Il livello come si legge nella lista e nella scheda dei soci.
 *
 * · con la scala dichiarata: la parola, oppure **«Da definire»** — per chi non
 *   ha livello e per un numero che la scala non chiama (il vecchio `0,5`);
 * · senza scala: il numero con la virgola, come prima. Senza parole dichiarate
 *   dal circolo non se ne inventano.
 */
export function livelloInParole(fasce, livello) {
    if (fasce.length === 0)
        return livello === null ? '—' : livello.toFixed(1).replace('.', ',');
    return parolaDelLivello(fasce, livello) ?? 'Da definire';
}
//# sourceMappingURL=livelli.js.map